import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useLayoutEffect, useState } from 'react';
import { Button, Modal, Radio, Switch } from 'antd';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import { useTranslation } from 'react-i18next';
import type { Preferences } from '@/api/preferences';
import { useAppearance } from '@/theme/appearanceContext';
import { useBreakpoints } from '@/features/shell/useViewport';
import { usePreferences } from '.';
import styles from './preferences.module.css';

interface PreferencesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Below 760 px Sign out lives here instead of the header (spec §2). */
  showSignOut: boolean;
  onSignOut: () => void;
  /** Focus goes back here on close (Safari does not focus a clicked button). */
  returnFocusRef?: RefObject<HTMLElement>;
}

const Group = ({
  legend,
  children,
}: {
  legend: string;
  children: ReactNode;
}) => (
  <fieldset className={styles.group}>
    <legend className={styles.legend}>{legend}</legend>
    {children}
  </fieldset>
);

const Option = ({
  label,
  description,
}: {
  label: string;
  description?: string;
}) => (
  <span className={styles.option}>
    <span>{label}</span>
    {description ? (
      <span className={styles.description}>{description}</span>
    ) : null}
  </span>
);

/**
 * Where the popover opens: 8 px under the Preferences button, its right edge
 * in line with the button's (kit Floating, placement bottom, align end).
 */
const usePopoverAnchor = (
  active: boolean,
  anchorRef: RefObject<HTMLElement> | undefined,
) => {
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(
    null,
  );
  useLayoutEffect(() => {
    if (!active) return undefined;
    const measure = () => {
      const box = anchorRef?.current?.getBoundingClientRect();
      if (!box) return;
      setAnchor({
        top: Math.round(box.bottom + 8),
        right: Math.max(8, Math.round(window.innerWidth - box.right)),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active, anchorRef]);
  return anchor;
};

/**
 * Every preference of spec §2: text size, appearance, language, width,
 * follow latest and reduce motion, plus the send key (§3). Changes apply at
 * once and are saved for the user on the server.
 */
const PreferencesDialog = ({
  open,
  onClose,
  showSignOut,
  onSignOut,
  returnFocusRef,
}: PreferencesDialogProps) => {
  const { t } = useTranslation();
  const { prefs, update } = usePreferences();
  const { system } = useAppearance();
  const osReducesMotion = system.prefersReducedMotion;
  // From 640 px the kit's anchored, full-height popover; below, a sheet.
  const { compact } = useBreakpoints();
  const anchor = usePopoverAnchor(open && !compact, returnFocusRef);
  const popover = !compact && anchor !== null;

  const signOut = showSignOut ? (
    <Button
      icon={<LogoutOutlined aria-hidden />}
      onClick={() => {
        onClose();
        onSignOut();
      }}
      data-testid="preferences-sign-out"
    >
      {t('v2.shell.signOut')}
    </Button>
  ) : null;

  const radio = <K extends keyof Preferences>(
    key: K,
    legend: string,
    options: { value: Preferences[K]; label: string; description?: string }[],
  ) => (
    <Group legend={legend}>
      <Radio.Group
        name={`pref-${key}`}
        value={prefs[key]}
        onChange={event =>
          update({ [key]: event.target.value } as Partial<Preferences>)
        }
        className={styles.radios}
      >
        {options.map(option => (
          <Radio
            key={String(option.value)}
            value={option.value}
            className={styles.radio}
          >
            <Option label={option.label} description={option.description} />
          </Radio>
        ))}
      </Radio.Group>
    </Group>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterClose={() => returnFocusRef?.current?.focus()}
      title={t('v2.shell.prefs')}
      width={popover ? 400 : 480}
      destroyOnClose={false}
      closable
      // The popover leaves the page visible, as the kit's does.
      mask={!popover}
      className={popover ? styles.popover : undefined}
      wrapClassName={popover ? styles.popoverWrap : undefined}
      style={
        popover
          ? ({
              '--pop-top': `${anchor.top}px`,
              '--pop-right': `${anchor.right}px`,
            } as CSSProperties)
          : undefined
      }
      footer={
        popover ? (
          signOut
        ) : (
          <div className={styles.footer}>
            {signOut ?? <span />}
            <Button type="primary" onClick={onClose}>
              {t('v2.common.close')}
            </Button>
          </div>
        )
      }
    >
      <div className={styles.body}>
        {radio('text_size', t('v2.shell.textSize'), [
          { value: 'small', label: t('v2.shell.ts_sl') },
          {
            value: 'medium',
            label: t('v2.shell.ts_ml'),
            description: t('v2.shell.textSizeDesc'),
          },
          { value: 'large', label: t('v2.shell.ts_ll') },
        ])}
        {radio('theme', t('v2.shell.appearance'), [
          { value: 'light', label: t('v2.shell.light') },
          { value: 'dark', label: t('v2.shell.dark') },
          { value: 'system', label: t('v2.shell.system') },
        ])}
        {/* Language names are autonyms, the same in both UIs. */}
        {radio('language', t('v2.shell.uiLang'), [
          { value: 'ja', label: '日本語' },
          { value: 'en', label: 'English' },
        ])}
        {radio('width', t('v2.shell.width'), [
          {
            value: 'standard',
            label: t('v2.shell.widthStandard'),
            description: t('v2.shell.widthStandardDesc'),
          },
          {
            value: 'wide',
            label: t('v2.shell.widthWide'),
            description: t('v2.shell.widthWideDesc'),
          },
        ])}
        {radio('send_key', t('v2.shell.sendKey'), [
          {
            value: 'ctrl_enter',
            label: t('v2.shell.sendKeyCtrl'),
            description: t('v2.shell.sendKeyCtrlDesc'),
          },
          {
            value: 'enter',
            label: t('v2.shell.sendKeyEnter'),
            description: t('v2.shell.sendKeyEnterDesc'),
          },
        ])}
        <div className={styles.switches}>
          <label className={styles.switchRow}>
            <Option
              label={t('v2.shell.followLatest')}
              description={t('v2.shell.followLatestDesc')}
            />
            <Switch
              checked={prefs.follow_latest}
              onChange={value => update({ follow_latest: value })}
              aria-label={t('v2.shell.followLatest')}
              data-testid="pref-follow-latest"
            />
          </label>
          <label className={styles.switchRow}>
            <Option
              label={t('v2.shell.reduceMotion')}
              description={
                osReducesMotion
                  ? t('v2.shell.reduceMotionOS')
                  : t('v2.shell.reduceMotionDesc')
              }
            />
            <Switch
              checked={prefs.reduce_motion || osReducesMotion}
              disabled={osReducesMotion}
              onChange={value => update({ reduce_motion: value })}
              aria-label={t('v2.shell.reduceMotion')}
              data-testid="pref-reduce-motion"
            />
          </label>
        </div>
      </div>
    </Modal>
  );
};

export default PreferencesDialog;
